/**
 * Gera um PNG recortado/rotacionado a partir de uma imagem e da área de recorte
 * devolvida pelo react-easy-crop (`croppedAreaPixels`).
 *
 * A saída tem tamanho fixo (`outputWidth` x `outputHeight`): a área recortada é
 * escalada para preencher exatamente esse tamanho, mantendo a proporção porque o
 * cropper já trava o `aspect` na mesma razão.
 */

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener("load", () => resolve(image))
    image.addEventListener("error", (error) => reject(error))
    image.src = url
  })

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180

/** Tamanho do bounding box de uma imagem depois de rotacionada. */
const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation)
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0, outputWidth, outputHeight) => {
  const image = await createImage(imageSrc)

  // 1) Canvas com a imagem já rotacionada (no tamanho do bounding box).
  const rotatedCanvas = document.createElement("canvas")
  const rotatedCtx = rotatedCanvas.getContext("2d")
  if (!rotatedCtx) {
    throw new Error("Falha ao processar imagem da assinatura")
  }

  const rotRad = getRadianAngle(rotation)
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation)

  rotatedCanvas.width = bBoxWidth
  rotatedCanvas.height = bBoxHeight

  rotatedCtx.translate(bBoxWidth / 2, bBoxHeight / 2)
  rotatedCtx.rotate(rotRad)
  rotatedCtx.translate(-image.width / 2, -image.height / 2)
  rotatedCtx.drawImage(image, 0, 0)

  // 2) Canvas final no tamanho fixo: escala a área recortada para preenchê-lo.
  const outputCanvas = document.createElement("canvas")
  const outputCtx = outputCanvas.getContext("2d")
  if (!outputCtx) {
    throw new Error("Falha ao processar imagem da assinatura")
  }

  outputCanvas.width = outputWidth
  outputCanvas.height = outputHeight

  outputCtx.drawImage(
    rotatedCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  )

  return new Promise((resolve, reject) => {
    outputCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({ blob, url: URL.createObjectURL(blob) })
        } else {
          reject(new Error("Falha ao gerar imagem da assinatura"))
        }
      },
      "image/png",
      1.0,
    )
  })
}

export default getCroppedImg
